import type { AxiosInstance } from 'axios';
import { useAuthClient } from '@auth/client/useAuthClient';

// The fixed, named set of members' module-scoped singleton Axios clients
// (created outside the React tree, some consumed via Redux saga context)
// that need auth headers attached. Named (rather than a plain array) so
// useAuthClient can be called explicitly once per client below — hooks
// can't be called from inside a loop/callback, so this shape is what lets
// each client get its own useAuthClient call while keeping the call count
// and order fixed across renders.
export type MembersAuthClients = {
  engageApi: AxiosInstance;
  engageApiTextResponseType: AxiosInstance;
  presenceApi: AxiosInstance;
  pagesApi: AxiosInstance;
  graphQlApi: AxiosInstance;
  tooltipsApi: AxiosInstance;
  guidanceApi: AxiosInstance;
  progressTrackingApi: AxiosInstance;
  contentPreferencesApi: AxiosInstance;
  userPreferencesApi: AxiosInstance;
  clubPelotonApi: AxiosInstance;
  reduxClient: AxiosInstance;
};

// Single source-of-truth attach logic shared with @auth/client and @studio/api:
// Auth0 token → checkout token → cookie-based session fallback when the Axios
// instance is configured with `withCredentials: true`, registered via
// useAuthClient's existingClient option onto each of members' existing singleton
// Axios clients instead of a freshly created one.
const useAuthorizationHeader = (clients: MembersAuthClients) => {
  useAuthClient({ existingClient: clients.engageApi });
  useAuthClient({ existingClient: clients.engageApiTextResponseType });
  useAuthClient({ existingClient: clients.presenceApi });
  useAuthClient({ existingClient: clients.pagesApi });
  useAuthClient({ existingClient: clients.graphQlApi });
  useAuthClient({ existingClient: clients.tooltipsApi });
  useAuthClient({ existingClient: clients.guidanceApi });
  useAuthClient({ existingClient: clients.progressTrackingApi });
  useAuthClient({ existingClient: clients.contentPreferencesApi });
  useAuthClient({ existingClient: clients.userPreferencesApi });
  useAuthClient({ existingClient: clients.clubPelotonApi });
  useAuthClient({ existingClient: clients.reduxClient });
};

export default useAuthorizationHeader;
